import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { copyToClipboard, findFileInRecursiveFolder } from '../utils/common';
import { Notification } from '@components/Notification';
import { DELETE_MODAL, RENAME_FILE_MODAL, RENAME_MODAL, UPLOAD_MODAL } from '@constants/common';
import config from 'cms/config.json';
import useDocumentCreation from './useDocumentCreation';
import { CMS_USER_ROLES } from '../utils/constants';

/**
 * Custom hook for generating menu options
 * @param {Function} openModal - Function to open modal
 * @param {Function} handleDuplicate - Function to handle file duplication
 * @param {Function} setTemplateUpdateTrigger - Function to trigger template update
 * @returns {Object} Menu option generators
 */
const useMenuOptions = (openModal, handleDuplicate, setTemplateUpdateTrigger) => {
  const dispatch = useDispatch();
  const userOrganizationId = useSelector((state) => state.user?.userOrganizationId);
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const workspaceFoldersList = useSelector((state) => state.organization?.workspaceFoldersList);
  const { createDocument } = useDocumentCreation();

  /**
   * Get folder option content for popover menu
   * @returns {Array} Folder options
   */
  const getFolderOptionContent = useCallback(() => {
    return [
      {
        slug: 'rename',
        element: 'Rename',
        clickHandler: (id) => {
          openModal(id, RENAME_FILE_MODAL, true);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'delete-folder',
        element: 'Delete',
        clickHandler: (id) => {
          openModal(id, DELETE_MODAL, true);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },

      {
        slug: 'copy-webhook',
        element: 'Copy webhook URL',
        clickHandler: (id) => {
          const url = `${config.BASE_WEBHOOK_URL}?organization_id=${userOrganizationId}&workspace_id=${activeWorkspace?.id}&folder_id=${id}`;
          copyToClipboard(url);
          Notification.success('Success!', 'Webhook URL copied.');
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'move_folder',
        element: 'Move',
        clickHandler: (id) => {
          openModal(id, 'MOVE_FILE', true, 'MOVE_FOLDER');
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'import-openapi',
        element: 'Import OpenAPI',
        clickHandler: (id) => {
          openModal(id, UPLOAD_MODAL, true);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
    ];
  }, [openModal, dispatch, userOrganizationId, activeWorkspace]);

  /**
   * Get file option content for popover menu
   * @returns {Array} File options
   */
  const getFileOptionContent = useCallback(() => {
    return [
      {
        slug: 'delete',
        element: 'Delete',
        clickHandler: (id) => {
          openModal(id, DELETE_MODAL, false);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'rename',
        element: 'Rename',
        clickHandler: (id) => {
          openModal(id, RENAME_FILE_MODAL, false);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'move_file',
        element: 'Move',
        clickHandler: (id) => {
          openModal(id, 'MOVE_FILE', false, 'MOVE_FILE');
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'copy_file',
        element: 'Copy',
        clickHandler: (id) => {
          openModal(id, 'MOVE_FILE', false, 'COPY_FILE');
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
    ];
  }, [openModal, dispatch]);

  /**
   * Get plus button content for popover menu
   * @returns {Array} Plus button options
   */
  const getPlusButtonContent = useCallback(() => {
    return [
      {
        element: 'New Collection',
        clickHandler: (id) => {
          openModal(id || activeWorkspace?.id, RENAME_MODAL, true);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        element: 'New Document',
        clickHandler: createDocument,
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
      {
        slug: 'import-openapi',
        element: 'Import API File',
        clickHandler: (id) => {
          openModal(id || activeWorkspace?.id, UPLOAD_MODAL, false);
        },
        permission: CMS_USER_ROLES.WORKSPACE_WRITE,
      },
    ];
  }, [openModal, activeWorkspace, dispatch]);

  return {
    getFolderOptionContent,
    getFileOptionContent,
    getPlusButtonContent,
  };
};

export default useMenuOptions;
